import { useQuery, useMutation } from "@tanstack/react-query";
import { Users, UserPlus, Trash2, Edit, UserX, UserCheck, Shield, ShieldOff, Power, PowerOff, Upload, X } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useToast } from "@/hooks/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { apiRequest, queryClient } from "@/lib/queryClient";
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Search } from "lucide-react";

const userSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Please enter a valid email address"),
  employeeId: z.string().optional().transform((val) => val?.trim()),
  phoneNumber: z.string().optional(),
  gender: z.enum(["male", "female", "other", "prefer_not_to_say"], {
    required_error: "Please select gender",
  }).optional(),
  dateOfJoining: z.string().min(1, "Date of joining is required"),
  department: z.string().min(1, "Please select a department"),
  designation: z.string().min(1, "Please select a designation"),
  manager: z.string().optional(),
  workLocation: z.string().min(1, "Please select work location"),
  buddy: z.string().optional(),
  role: z.enum(["admin", "employee", "candidate"], {
    required_error: "Please select a user type",
  }),
  profilePictureUrl: z.string().optional(),
});

type UserForm = z.infer<typeof userSchema>;

interface User {
  id: number;
  employeeId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  gender?: string;
  dateOfJoining?: string;
  department?: string;
  designation?: string;
  manager?: string;
  managerUserId?: number;
  buddy?: string;
  buddyUserId?: number;
  inductionStatus?: string;
  workLocation?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  role: string;
  username: string;
  isActive: boolean;
  isBlocked: boolean;
  profilePictureUrl?: string;
}

export default function ManageUsers() {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [isEditUserOpen, setIsEditUserOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: users, isLoading, error } = useQuery({
    queryKey: ['/admin/users'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/users");
      const data = await response.json();
      // Debug: Log user data to check if profilePictureUrl is present
      if (data?.users) {
        console.log('Users data received:', data.users);
        data.users.forEach((user: any, index: number) => {
          if (user.profilePictureUrl) {
            console.log(`User ${index} (${user.firstName} ${user.lastName}) has profilePictureUrl:`, user.profilePictureUrl);
          }
        });
      }
      return data;
    },
  });


  // Fetch users for manager/buddy dropdowns
  const { data: dropdownUsers } = useQuery({
    queryKey: ['/admin/users/dropdown'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/users/dropdown");
      return response.json();
    },
  });

  // Fetch master data for dropdowns
  const { data: departments } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  });

  const { data: designations } = useQuery({
    queryKey: ['/admin/designations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/designations");
      return response.json();
    },
  });

  const { data: officeLocations } = useQuery({
    queryKey: ['/admin/office-locations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/office-locations");
      return response.json();
    },
  });

  // Reset to first page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Update image preview when editing user changes or dialog opens
  useEffect(() => {
    console.log('🔄 useEffect triggered - isEditUserOpen:', isEditUserOpen, 'editingUser:', editingUser);
    if (isEditUserOpen && editingUser) {
      const profilePicUrl = editingUser.profilePictureUrl;
      if (profilePicUrl && typeof profilePicUrl === 'string' && profilePicUrl.trim() !== '') {
        console.log('✅ useEffect: Setting image preview from editingUser:', profilePicUrl);
        setImagePreview(profilePicUrl);
      } else {
        console.log('❌ useEffect: No valid profile picture URL for editing user');
        setImagePreview(null);
      }
    } else if (!isEditUserOpen) {
      // Reset when dialog closes
      console.log('🔄 Dialog closed - resetting image state');
      setImagePreview(null);
      setSelectedImage(null);
    }
  }, [editingUser, isEditUserOpen]);

  const form = useForm<UserForm>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      gender: undefined,
      dateOfJoining: "",
      department: "",
      designation: "",
      manager: "",
      workLocation: "",
      buddy: "",
      role: undefined,
    },
  });



  // Upload image to Firebase
  const uploadImageToFirebase = async (file: File): Promise<string> => {
    try {
      setIsUploading(true);
      console.log('📤 Starting image upload:', file.name, file.size, 'bytes');
      
      const { storage, ensureAuthenticated } = await import('@/lib/firebase');
      const { ref, uploadBytes, getDownloadURL } = await import('firebase/storage');
      
      console.log('🔐 Ensuring authentication...');
      await ensureAuthenticated();
      console.log('✅ Authentication successful');
      
      const timestamp = Date.now();
      const fileName = `user-photographs/${timestamp}-${file.name}`;
      console.log('📁 Uploading to:', fileName);
      
      const storageRef = ref(storage, fileName);
      
      console.log('⬆️ Uploading bytes...');
      const snapshot = await uploadBytes(storageRef, file, {
        contentType: file.type
      });
      console.log('✅ Upload complete, getting download URL...');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('✅ Download URL obtained:', downloadURL.substring(0, 50) + '...');
      return downloadURL;
    } catch (error: any) {
      console.error("❌ Firebase upload error:", error);
      console.error("Error details:", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      
      let errorMessage = "Failed to upload image. Please try again.";
      if (error?.code === 'storage/unauthorized') {
        errorMessage = "Authentication failed. Please refresh the page and try again.";
      } else if (error?.code === 'storage/quota-exceeded') {
        errorMessage = "Storage quota exceeded. Please contact administrator.";
      } else if (error?.message) {
        errorMessage = `Upload failed: ${error.message}`;
      }
      
      toast({
        title: "Upload Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      throw new Error(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: Partial<UserForm> }) => {
      console.log('Updating user with ID:', id);
      console.log('Update data:', data);
      
      let profilePictureUrl = data.profilePictureUrl;
      
      // Upload image if selected
      if (selectedImage) {
        try {
          console.log('📤 Uploading new image...');
          profilePictureUrl = await uploadImageToFirebase(selectedImage);
          console.log('✅ Image uploaded successfully:', profilePictureUrl?.substring(0, 50));
        } catch (uploadError: any) {
          console.error('❌ Image upload failed in mutation:', uploadError);
          // The uploadImageToFirebase already shows a toast, but we should still throw
          throw uploadError;
        }
      }
      
      // Handle email updates based on role
      const updateData = { ...data };
      if (data.email && data.role) {
        if (data.role === 'employee') {
          (updateData as any).companyEmail = data.email;
          (updateData as any).username = data.email;
          // Don't update personalEmail for employees
        } else if (data.role === 'candidate') {
          (updateData as any).personalEmail = data.email;
          (updateData as any).username = data.email;
          // Don't update companyEmail for candidates
        }
        // Remove the email field as we're mapping it to the appropriate field
        delete updateData.email;
      }
      
      // Add profile picture URL if available
      if (profilePictureUrl) {
        (updateData as any).profilePictureUrl = profilePictureUrl;
      }
      
      const response = await apiRequest("PUT", `/admin/users/${id}`, updateData);
      console.log('Update response:', response);
      return response.json();
    },
    onSuccess: (data) => {
      // Check if calendar events were created (when converting candidate to employee)
      if (data.user?.calendarEvents) {
        toast({
          title: "Success! 🎉",
          description: "User updated successfully! Google Calendar meeting has been scheduled and invites sent.",
        });
        
        // Show calendar events info
        setTimeout(() => {
          toast({
            title: "📅 Calendar Event Created",
            description: `Onboarding meeting scheduled for ${data.user.firstName} ${data.user.lastName}.`,
          });
        }, 1000);
      } else {
        toast({
          title: "Success",
          description: "User updated successfully",
        });
      }
      setIsEditUserOpen(false);
      setEditingUser(null);
      setSelectedImage(null);
      setImagePreview(null);
      form.reset();
      queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
    },
    onError: (error: Error) => {
      console.error('❌ Update user mutation error:', error);
      // Only show toast if it's not an upload error (upload already shows its own toast)
      if (!error.message.toLowerCase().includes('upload') && !error.message.toLowerCase().includes('image')) {
        toast({
          title: "Error Updating User",
          description: error.message || "Failed to update user. Please try again.",
          variant: "destructive",
        });
      }
      // Reset uploading state on error
      setIsUploading(false);
    },
  });

  const toggleUserStatusMutation = useMutation({
    mutationFn: async ({ id, type }: { id: number; type: 'activate' | 'deactivate' | 'block' | 'unblock' }) => {
      const response = await apiRequest("PATCH", `/admin/users/${id}/status`, { type });
      return response.json();
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Success",
        description: `Employee ${variables.type === 'block' ? 'blocked' : variables.type === 'unblock' ? 'unblocked' : variables.type + 'd'} successfully`,
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      const response = await apiRequest("DELETE", `/admin/users/${userId}`);
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Employee deleted successfully",
      });
      queryClient.invalidateQueries({ queryKey: ['/admin/users'] });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmitUser = (data: UserForm) => {
    console.log('Form submitted with data:', data);
    console.log('Editing user:', editingUser);
    if (editingUser) {
      updateUserMutation.mutate({ id: editingUser.id, data });
    } else {
      console.error('No editing user found');
    }
  };

  const handleEditUser = (user: User) => {
    console.log('🔍 handleEditUser called with user:', user);
    console.log('🔍 user.profilePictureUrl:', user.profilePictureUrl);
    console.log('🔍 typeof user.profilePictureUrl:', typeof user.profilePictureUrl);
    
    setEditingUser(user);
    
    // Reset image state and set existing image preview IMMEDIATELY
    setSelectedImage(null);
    
    // Set image preview from user's profile picture URL
    const profilePicUrl = user.profilePictureUrl;
    if (profilePicUrl && typeof profilePicUrl === 'string' && profilePicUrl.trim() !== '') {
      console.log('✅ Setting image preview to:', profilePicUrl);
      setImagePreview(profilePicUrl);
    } else {
      console.log('❌ No valid profile picture URL - setting to null');
      setImagePreview(null);
    }
    
    // Format the date properly
    let formattedDate = "";
    if (user.dateOfJoining) {
      try {
        const date = new Date(user.dateOfJoining);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString().split('T')[0];
        }
      } catch (error) {
        console.error('Error formatting date:', error);
      }
    }
    
    form.reset({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      employeeId: user.employeeId || "",
      phoneNumber: user.phoneNumber || "",
      gender: user.gender as any || undefined,
      dateOfJoining: formattedDate,
      department: user.department as any || undefined,
      designation: user.designation as any || undefined,
      manager: user.managerUserId?.toString() || "",
      workLocation: user.workLocation as any || undefined,
      buddy: user.buddyUserId?.toString() || "",
      role: user.role as any || undefined,
      profilePictureUrl: user.profilePictureUrl || "",
    });
    setIsEditUserOpen(true);
  };

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select an image file",
          variant: "destructive",
        });
        return;
      }
      
      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Image must be less than 5MB",
          variant: "destructive",
        });
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    form.setValue('profilePictureUrl', '');
  };

  const getStatusBadge = (user: User) => {
    if (user.isBlocked) {
      return <Badge variant="destructive">Blocked</Badge>;
    }
    if (!user.isActive) {
      return <Badge variant="secondary">Inactive</Badge>;
    }
    return <Badge variant="default">Active</Badge>;
  };

  const getInductionStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-green-100 text-green-800">Completed</Badge>;
      case 'in_progress':
        return <Badge variant="default" className="bg-yellow-100 text-yellow-800">In Progress</Badge>;
      default:
        return null; // Don't show anything for pending status
    }
  };

  if (isLoading) return <div className="text-center py-8">Loading employees...</div>;

  // Get the users array from the response
  const usersArray = (users as any)?.users || [];
  
  // Filter users based on search query
  const filteredUsers = usersArray.filter((user: User) => {
    if (!searchQuery) return true;
    
    const query = searchQuery.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(query) ||
      user.lastName?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.employeeId?.toLowerCase().includes(query) ||
      user.department?.toLowerCase().includes(query) ||
      user.designation?.toLowerCase().includes(query) ||
      user.manager?.toLowerCase().includes(query) ||
      user.workLocation?.toLowerCase().includes(query) ||
      user.role?.toLowerCase().includes(query)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredUsers.slice(startIndex, endIndex);

  return (
    <div className="space-y-6" data-testid="manage-users">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Manage Employees</h1>
          <p className="text-gray-600">Add, edit, and manage employee information</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            onClick={() => setLocation('/bulk-upload')}
            data-testid="button-bulk-upload"
            variant="outline"
            className="border-servify-purple text-servify-purple hover:bg-servify-purple hover:text-white"
          >
            <Upload className="mr-2 h-4 w-4" />
            Bulk Upload
          </Button>
          <Button 
            onClick={() => setLocation('/add-employee')}
            data-testid="button-add-user"
            className="bg-servify-purple hover:bg-servify-purple-dark text-white"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            Add Employee
          </Button>
        </div>

        <Dialog open={isEditUserOpen} onOpenChange={(open) => {
          setIsEditUserOpen(open);
          if (!open) {
            setEditingUser(null);
            setSelectedImage(null);
            setImagePreview(null);
            form.reset();
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col bg-white">
            <DialogHeader className="pb-6 border-b border-gray-200">
              <DialogTitle className="text-2xl font-bold text-gray-900">Edit Employee</DialogTitle>
              <DialogDescription className="text-gray-600 mt-2">Update the employee's information.</DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto p-6">
              <UserForm 
                form={form} 
                onSubmit={onSubmitUser} 
                isSubmitting={updateUserMutation.isPending || isUploading}
                isEditing={true}
                onCancel={() => {
                  setIsEditUserOpen(false);
                  setSelectedImage(null);
                  setImagePreview(null);
                }}
                imagePreview={imagePreview}
                selectedImage={selectedImage}
                onImageSelect={handleImageSelect}
                onRemoveImage={removeImage}
                fileInputRef={fileInputRef}
              />
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {error && (
        <div className="text-red-600 p-4 border border-red-300 rounded">
          Error loading employees: {(error as any)?.message || 'Unknown error'}
        </div>
      )}

      <Card className="border-0 shadow-lg">
        <CardHeader className="px-0">
          <div className="flex items-center">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                type="text"
                placeholder="Search employees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 w-80 h-12 bg-white border-gray-300 focus:border-servify-purple focus:ring-1 focus:ring-servify-purple text-base"
                data-testid="search-employees"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="px-0">
          <div className="space-y-4">
            {currentUsers.map((user: User) => (
              <div
                key={user.id}
                className="flex items-center justify-between p-6 border border-gray-300 rounded-lg bg-white shadow-lg"
                data-testid={`user-item-${user.id}`}
              >
                <div className="flex items-center space-x-4 flex-1">
                  {user.profilePictureUrl ? (
                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-servify-purple/20 flex-shrink-0">
                      <img
                        src={user.profilePictureUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            parent.innerHTML = `
                              <div class="w-12 h-12 bg-servify-purple rounded-full flex items-center justify-center text-white font-semibold text-lg">
                                ${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}
                              </div>
                            `;
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="w-12 h-12 bg-servify-purple rounded-full flex items-center justify-center text-white font-semibold text-lg flex-shrink-0">
                      {user.firstName?.[0]}{user.lastName?.[0]}
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900 text-lg">
                        {user.firstName} {user.lastName}
                      </h4>
                      {getStatusBadge(user)}
                      {user.inductionStatus && getInductionStatusBadge(user.inductionStatus)}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-gray-600">
                      <p><strong>Employee ID:</strong> {user.employeeId}</p>
                      <p><strong>Email:</strong> {user.email}</p>
                      <p><strong>Department:</strong> {user.department}</p>
                      <p><strong>Designation:</strong> {user.designation}</p>
                      <p><strong>Manager:</strong> {user.manager}</p>
                      <p><strong>Location:</strong> {user.workLocation}</p>
                      <p><strong>User Type:</strong> {user.role.charAt(0).toUpperCase() + user.role.slice(1)}</p>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        user.role === 'admin' ? 'bg-servify-purple/20 text-servify-purple' :
                        user.role === 'employee' ? 'bg-servify-purple/20 text-servify-purple' :
                        'bg-servify-purple/20 text-servify-purple'
                      }`}>
                        {user.role.charAt(0).toUpperCase() + user.role.slice(1)}
                      </span>
                    </div>
                  </div>
                </div>
                <TooltipProvider>
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditUser(user)}
                          data-testid={`button-edit-user-${user.id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                                              <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                          <p>Edit</p>
                        </TooltipContent>
                    </Tooltip>
                    
                    {user.isBlocked ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleUserStatusMutation.mutate({ id: user.id, type: 'unblock' })}
                            disabled={toggleUserStatusMutation.isPending}
                            data-testid={`button-unblock-user-${user.id}`}
                          >
                            <ShieldOff className="h-4 w-4 text-green-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                          <p>Unblock</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleUserStatusMutation.mutate({ id: user.id, type: 'block' })}
                            disabled={toggleUserStatusMutation.isPending}
                            data-testid={`button-block-user-${user.id}`}
                          >
                            <Shield className="h-4 w-4 text-orange-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                          <p>Block</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    {user.isActive ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleUserStatusMutation.mutate({ id: user.id, type: 'deactivate' })}
                            disabled={toggleUserStatusMutation.isPending}
                            data-testid={`button-deactivate-user-${user.id}`}
                          >
                            <PowerOff className="h-4 w-4 text-gray-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                          <p>Deactivate</p>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleUserStatusMutation.mutate({ id: user.id, type: 'activate' })}
                            disabled={toggleUserStatusMutation.isPending}
                            data-testid={`button-activate-user-${user.id}`}
                          >
                            <Power className="h-4 w-4 text-green-600" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                          <p>Activate</p>
                        </TooltipContent>
                      </Tooltip>
                    )}

                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setUserToDelete(user)}
                          disabled={deleteUserMutation.isPending}
                          data-testid={`button-delete-user-${user.id}`}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent className="bg-gray-900 text-white border-0 px-2 py-1 text-sm">
                        <p>Delete</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              </div>
            ))}
            {filteredUsers.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                {searchQuery ? `No employees found matching "${searchQuery}"` : "No employees found."}
              </p>
            )}

            {/* Pagination Controls */}
            {filteredUsers.length > 0 && totalPages > 1 && (
              <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} employees
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="button-prev-page"
                  >
                    Previous
                  </Button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(page)}
                        className={currentPage === page ? "bg-servify-purple text-white" : ""}
                        data-testid={`button-page-${page}`}
                      >
                        {page}
                      </Button>
                    ))}
                  </div>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    data-testid="button-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
        <AlertDialogContent className="bg-white border-2 border-gray-300 shadow-2xl max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold text-gray-900">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="text-base text-gray-700 mt-3 leading-relaxed">
              Are you sure you want to delete <span className="font-semibold text-gray-900">{userToDelete?.firstName} {userToDelete?.lastName}</span>? This action cannot be undone and will permanently remove all associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6">
            <AlertDialogCancel 
              onClick={() => setUserToDelete(null)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 border-2 border-gray-300 font-semibold px-6"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (userToDelete) {
                  deleteUserMutation.mutate(userToDelete.id);
                  setUserToDelete(null);
                }
              }}
              className="bg-red-600 hover:bg-red-700 text-white font-semibold px-6 shadow-lg"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

interface UserFormProps {
  form: any;
  onSubmit: (data: UserForm) => void;
  isSubmitting: boolean;
  isEditing: boolean;
  onCancel: () => void;
  imagePreview?: string | null;
  selectedImage?: File | null;
  onImageSelect?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveImage?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement>;
}

function UserForm({ form, onSubmit, isSubmitting, isEditing, onCancel, imagePreview, selectedImage, onImageSelect, onRemoveImage, fileInputRef }: UserFormProps) {
  // Get profilePictureUrl from form as fallback
  const formProfilePictureUrl = form.watch('profilePictureUrl');
  
  // Use imagePreview prop or fallback to form value
  const displayImageUrl = imagePreview || (formProfilePictureUrl && formProfilePictureUrl.trim() !== '' ? formProfilePictureUrl : null);
  
  // Debug: Log imagePreview to see if it's being passed correctly
  useEffect(() => {
    console.log('📸 UserForm render - imagePreview prop:', imagePreview);
    console.log('📸 UserForm render - formProfilePictureUrl:', formProfilePictureUrl);
    console.log('📸 UserForm render - displayImageUrl:', displayImageUrl);
    console.log('📸 UserForm render - isEditing:', isEditing);
    console.log('📸 UserForm render - Will show image?', !!displayImageUrl);
  }, [imagePreview, formProfilePictureUrl, displayImageUrl, isEditing]);
  

  // Fetch users for manager/buddy dropdowns
  const { data: dropdownUsers } = useQuery({
    queryKey: ['/admin/users/dropdown'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/users/dropdown");
      return response.json();
    },
  });

  // Fetch master data for dropdowns
  const { data: departments } = useQuery({
    queryKey: ['/admin/departments'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/departments");
      return response.json();
    },
  });

  const { data: designations } = useQuery({
    queryKey: ['/admin/designations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/designations");
      return response.json();
    },
  });

  const { data: officeLocations } = useQuery({
    queryKey: ['/admin/office-locations'],
    queryFn: async () => {
      const response = await apiRequest("GET", "/admin/office-locations");
      return response.json();
    },
  });

  return (
    <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit, (errors: any) => {
          console.log('Form validation errors:', errors);
        })} className="space-y-8">
        {/* Personal Details */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 border-b-2 border-servify-purple pb-3">Personal Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">First Name</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-first-name" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Last Name</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-last-name" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" data-testid="input-email" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Employee ID (Optional)</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-employee-id" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" placeholder="e.g., EMP001" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Profile Picture Upload */}
          <FormField
            control={form.control}
            name="profilePictureUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-base font-semibold text-gray-800 mb-2">Profile Picture (Optional)</FormLabel>
                <FormControl>
                  <div className="space-y-4">
                    {displayImageUrl ? (
                      <div className="relative inline-block">
                        <img
                          src={displayImageUrl}
                          alt="Profile preview"
                          className="w-32 h-32 object-cover rounded-lg border-2 border-gray-200 bg-gray-100"
                          onError={(e) => {
                            console.error('❌ Failed to load image:', displayImageUrl);
                            const target = e.target as HTMLImageElement;
                            const parent = target.parentElement;
                            if (parent) {
                              target.style.display = 'none';
                              const errorMsg = document.createElement('div');
                              errorMsg.className = 'text-red-500 text-xs p-2 bg-red-50 rounded';
                              errorMsg.textContent = 'Image failed to load';
                              parent.appendChild(errorMsg);
                            }
                          }}
                          onLoad={() => {
                            console.log('✅ Image loaded successfully:', displayImageUrl);
                          }}
                        />
                        <button
                          type="button"
                          onClick={onRemoveImage}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 z-10"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <div className="mt-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef?.current?.click()}
                            disabled={isSubmitting}
                            className="text-xs"
                          >
                            Change Image
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-2">Upload profile picture</p>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => fileInputRef?.current?.click()}
                          disabled={isSubmitting}
                          className="text-sm"
                        >
                          {isSubmitting ? "Uploading..." : "Choose File"}
                        </Button>
                        <p className="text-xs text-gray-500 mt-2">Max 5MB, JPG/PNG</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={onImageSelect}
                      className="hidden"
                      data-testid="input-profile-picture"
                    />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="phoneNumber"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Phone Number (Optional)</FormLabel>
                  <FormControl>
                    <Input {...field} data-testid="input-phone" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="gender"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Gender (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-gender" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="male" className="hover:bg-gray-50 cursor-pointer">Male</SelectItem>
                      <SelectItem value="female" className="hover:bg-gray-50 cursor-pointer">Female</SelectItem>
                      <SelectItem value="other" className="hover:bg-gray-50 cursor-pointer">Other</SelectItem>
                      <SelectItem value="prefer_not_to_say" className="hover:bg-gray-50 cursor-pointer">Prefer not to say</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        {/* Employment Details */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-gray-900 border-b-2 border-servify-purple pb-3">Employment Details</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="dateOfJoining"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Date of Joining</FormLabel>
                  <FormControl>
                    <Input {...field} type="date" data-testid="input-date-joining" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="department"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Department</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-department" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select department" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      {departments?.map((dept: any) => (
                        <SelectItem 
                          key={dept.id} 
                          value={dept.name} 
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          {dept.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="designation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Designation</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-designation" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select designation" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      {designations?.map((designation: any) => (
                        <SelectItem 
                          key={designation.id} 
                          value={designation.title} 
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          {designation.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="manager"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Manager</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-manager" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select manager" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      {dropdownUsers?.map((user: any) => (
                        <SelectItem 
                          key={user.id} 
                          value={user.id.toString()} 
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          {`${user.firstName} ${user.lastName}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="workLocation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Work Location</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-work-location" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select work location" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      {officeLocations?.map((location: any) => (
                        <SelectItem 
                          key={location.id} 
                          value={location.name} 
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          {location.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="buddy"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">Buddy (Optional)</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-buddy" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select buddy" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      {dropdownUsers?.map((user: any) => (
                        <SelectItem 
                          key={user.id} 
                          value={user.id.toString()} 
                          className="hover:bg-gray-50 cursor-pointer"
                        >
                          {`${user.firstName} ${user.lastName}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-base font-semibold text-gray-800 mb-2">User Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger data-testid="select-user-role" className="h-12 border-2 border-gray-300 focus:border-servify-purple focus:ring-2 focus:ring-servify-purple/20 text-base">
                        <SelectValue placeholder="Select user type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg">
                      <SelectItem value="admin" className="hover:bg-gray-50 cursor-pointer">Admin</SelectItem>
                      <SelectItem value="employee" className="hover:bg-gray-50 cursor-pointer">Employee</SelectItem>
                      <SelectItem value="candidate" className="hover:bg-gray-50 cursor-pointer">Candidate</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end gap-4 pt-8 border-t-2 border-gray-200">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            data-testid="button-cancel"
            className="h-12 px-6 text-base font-semibold border-2 border-gray-300 hover:border-gray-400"
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            data-testid="button-submit-user"
            className="bg-servify-purple hover:bg-servify-purple-dark text-white h-12 px-8 text-base font-semibold shadow-lg"
          >
            {isSubmitting ? "Updating..." : "Update"}
          </Button>
        </div>
      </form>
    </Form>
  );
}